<template>
  <div class="csx-page">
    <div class="csx-header">
      <div class="csx-header-top">
        <div>
          <div class="csx-breadcrumb">
            <span>خانه</span>
            <span class="csx-breadcrumb-sep">‹</span>
            <span>دسته‌بندی‌ها</span>
            <template v-if="selectedTreeNode">
              <span class="csx-breadcrumb-sep">‹</span>
              <span class="csx-breadcrumb-current">{{ selectedTreeNode.name }}</span>
            </template>
          </div>
          <h1 class="csx-title">تنظیمات سئوی دسته‌بندی</h1>
          <p class="csx-subtitle">بهینه‌سازی اطلاعات سئو برای دسته‌بندی انتخاب‌شده</p>
        </div>
        <button type="button" class="csx-btn-secondary" @click="showHelp = true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 2-3 4" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          راهنما
        </button>
      </div>
    </div>

    <button type="button" class="csx-mobile-trigger" @click="mobileDrawerOpen = true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
      </svg>
      <span>انتخاب دسته‌بندی</span>
      <span v-if="selectedTreeNode" class="csx-mobile-trigger-name">{{ selectedTreeNode.name }}</span>
    </button>

    <div class="csx-layout">
      <aside class="csx-panel-slot">
        <category-seo-picker-panel
            ref="pickerPanel"
            :tree="tree"
            :selected-id="selectedId"
            :selected-node="selectedTreeNode"
            :selected-path="selectedPath"
            :loading="loadingTree"
            @select="onPickCategory"
        />
      </aside>

      <main class="csx-main">
        <div v-if="!selectedId" class="csx-empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
            <circle cx="11" cy="12.5" r="2.5" />
            <path d="m14.5 15.5 2 2" />
          </svg>
          <h3>یک دسته‌بندی انتخاب کنید</h3>
          <p>برای مشاهده و ویرایش اطلاعات سئو، ابتدا یک دسته‌بندی را از لیست انتخاب کنید.</p>
        </div>

        <template v-else-if="loadingDetail">
          <div class="csx-card csx-skeleton-card">
            <div class="csx-skeleton-bar" style="width: 40%; height: 16px"></div>
            <div class="csx-skeleton-bar" style="width: 100%; height: 44px; margin-top: 14px"></div>
            <div class="csx-skeleton-bar" style="width: 100%; height: 80px; margin-top: 14px"></div>
            <div class="csx-skeleton-bar" style="width: 100%; height: 140px; margin-top: 14px"></div>
          </div>
        </template>

        <template v-else>
          <section class="csx-card">
            <div class="csx-card-header">
              <div class="csx-card-header-icon icon-target">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
              </div>
              <div>
                <div class="csx-card-title">اطلاعات سئو</div>
                <div class="csx-card-subtitle">عنوان و توضیحات متا برای موتورهای جستجو</div>
              </div>
            </div>

            <div class="csx-field">
              <label class="csx-label">عنوان سئو (Meta Title) <span class="csx-required">*</span></label>
              <input v-model="form.meta_title" maxlength="60" class="csx-input">
              <div class="csx-field-footer">
                <span class="csx-helper">عنوانی جذاب و مرتبط بنویسید. پیشنهاد ما بین ۵۰ تا ۶۰ کاراکتر است.</span>
                <span class="csx-counter" :class="titleCounterClass">{{ titleLen }} / 60</span>
              </div>
            </div>

            <div class="csx-field">
              <label class="csx-label">توضیحات متا (Meta Description) <span class="csx-required">*</span></label>
              <textarea v-model="form.meta_description" maxlength="160" rows="3" class="csx-textarea"></textarea>
              <div class="csx-field-footer">
                <span class="csx-helper">توضیح کوتاه و جذاب درباره این دسته‌بندی بنویسید. پیشنهاد ما بین ۱۳۰ تا ۱۶۰ کاراکتر است.</span>
                <span class="csx-counter" :class="descCounterClass">{{ descLen }} / 160</span>
              </div>
            </div>

            <div class="csx-field">
              <label class="csx-label">توضیحات دسته‌بندی</label>
              <div class="csx-editor-wrap">
                <quill-editor v-model="form.explanation" :options="editorOptions" @ready="onEditorReady"></quill-editor>
              </div>
              <div class="csx-field-footer">
                <span class="csx-helper">این توضیحات در پایین صفحه دسته‌بندی نمایش داده می‌شود و به بهبود سئو و تجربه کاربر کمک می‌کند.</span>
                <span class="csx-counter">{{ explanationTextLength }} / 5000</span>
              </div>
            </div>
          </section>

          <section class="csx-card">
            <div class="csx-section-title">تنظیمات موتورهای جستجو</div>

            <div class="csx-switches-grid">
              <div class="csx-switch-row">
                <div class="csx-switch-control">
                  <button
                      type="button"
                      class="csx-switch"
                      :class="{ on: !form.is_indexed }"
                      @click="form.is_indexed = !form.is_indexed"
                  ><span class="csx-switch-thumb"></span></button>
                  <div>
                    <div class="csx-switch-label">عدم ایندکس شدن <span class="csx-switch-tech">noindex</span></div>
                    <div class="csx-switch-desc">از موتورهای جستجو می‌خواهد این صفحه را در نتایج جستجو نمایش ندهند.</div>
                  </div>
                </div>
                <div v-if="!form.is_indexed" class="csx-risk-warning">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  با فعال کردن این گزینه، صفحه ممکن است از نتایج گوگل حذف شود.
                </div>
              </div>

              <div class="csx-switch-row">
                <div class="csx-switch-control">
                  <button
                      type="button"
                      class="csx-switch"
                      :class="{ on: !form.is_followed }"
                      @click="form.is_followed = !form.is_followed"
                  ><span class="csx-switch-thumb"></span></button>
                  <div>
                    <div class="csx-switch-label">عدم دنبال کردن لینک‌ها <span class="csx-switch-tech">nofollow</span></div>
                    <div class="csx-switch-desc">به موتور جستجو اعلام می‌کند لینک‌های موجود در این صفحه را دنبال نکند.</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section class="csx-card">
            <div class="csx-section-title">پیش‌نمایش گوگل</div>
            <p class="csx-card-desc">نمای تقریبی نحوه نمایش این دسته در نتایج جستجو</p>
            <div class="csx-google-preview">
              <div class="csx-google-url">{{ previewUrl }}</div>
              <div class="csx-google-title">{{ previewTitle }}</div>
              <div class="csx-google-desc">{{ previewDescription }}</div>
            </div>
          </section>

          <section class="csx-card">
            <div class="csx-score-header">
              <div class="csx-section-title" style="margin: 0">وضعیت سئو</div>
              <div class="csx-score-badge" :style="{ color: seoScoreColor, borderColor: seoScoreColor }">{{ seoScore }}</div>
            </div>
            <ul class="csx-score-checks">
              <li v-for="check in seoChecks" :key="check.id" :class="{ pass: check.pass }">
                <svg v-if="check.pass" class="csx-check-icon ok" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <svg v-else class="csx-check-icon no" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                {{ check.label }}
              </li>
            </ul>
          </section>
        </template>
      </main>
    </div>

    <div class="csx-save-bar">
      <div class="csx-save-status" :class="saveStatusClass">
        <span v-if="saving" class="csx-spinner"></span>
        {{ saveStatusText }}
      </div>
      <button type="button" class="csx-save-btn" :disabled="saving || !selectedId" @click="save()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
          <polyline points="17 21 17 13 7 13 7 21" />
          <polyline points="7 3 7 8 15 8" />
        </svg>
        ذخیره تغییرات
      </button>
    </div>

    <div v-if="mobileDrawerOpen" class="csx-drawer-overlay" @click.self="mobileDrawerOpen = false">
      <div class="csx-drawer">
        <div class="csx-drawer-header">
          <span>انتخاب دسته‌بندی</span>
          <button type="button" class="csx-icon-btn" @click="mobileDrawerOpen = false">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <category-seo-picker-panel
            :tree="tree"
            :selected-id="selectedId"
            :selected-node="selectedTreeNode"
            :selected-path="selectedPath"
            :loading="loadingTree"
            @select="onPickCategory"
        />
      </div>
    </div>

    <div v-if="confirmDialog.open" class="csx-modal-overlay">
      <div class="csx-modal">
        <div class="csx-modal-title">تغییرات ذخیره نشده</div>
        <div class="csx-modal-desc">قبل از رفتن به دسته‌بندی دیگر، تغییرات فعلی را ذخیره کنید.</div>
        <div class="csx-modal-actions">
          <button type="button" class="csx-modal-btn primary" @click="confirmSaveAndContinue">ذخیره و ادامه</button>
          <button type="button" class="csx-modal-btn" @click="confirmContinueWithoutSaving">ادامه بدون ذخیره</button>
          <button type="button" class="csx-modal-btn ghost" @click="confirmCancel">انصراف</button>
        </div>
      </div>
    </div>

    <div v-if="showHelp" class="csx-modal-overlay" @click.self="showHelp = false">
      <div class="csx-modal">
        <div class="csx-modal-title">راهنما</div>
        <div class="csx-modal-desc csx-help-text">
          <p>از لیست سمت راست یک دسته‌بندی را انتخاب کنید تا اطلاعات سئوی آن را ویرایش کنید.</p>
          <p>«عنوان سئو» و «توضیحات متا» فقط برای گوگل استفاده می‌شوند و نام واقعی دسته‌بندی را تغییر نمی‌دهند.</p>
          <p>«توضیحات دسته‌بندی» زیر لیست محصولات در فروشگاه نمایش داده می‌شود.</p>
          <p>کلید میانبر ذخیره: <b>Ctrl+S</b> — کلید میانبر جستجوی دسته‌بندی: <b>/</b></p>
        </div>
        <div class="csx-modal-actions">
          <button type="button" class="csx-modal-btn primary" @click="showHelp = false">متوجه شدم</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CategorySeoPickerPanel from "./CategorySeoPickerPanel";
import { quillEditor } from "vue-quill-editor";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";

const STORE_BASE_URL = "https://exuni.ir";

export default {
  name: "CategorySeoSettings",
  components: { CategorySeoPickerPanel, quillEditor },
  data() {
    return {
      tree: [],
      loadingTree: false,
      loadingDetail: false,
      saving: false,
      selectedId: null,
      form: this.emptyForm(),
      originalForm: null,
      mobileDrawerOpen: false,
      showHelp: false,
      confirmDialog: { open: false, pendingNode: null },
      editorOptions: {
        theme: "snow",
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ align: [] }, { direction: "rtl" }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ indent: "-1" }, { indent: "+1" }],
            ["blockquote", "code-block"],
            ["link"],
            ["clean"],
          ],
        },
      },
    };
  },
  computed: {
    selectedTreeNode() {
      return this.selectedId ? this.findNodeById(this.tree, this.selectedId) : null;
    },
    selectedPath() {
      return this.selectedId ? this.findPathNames(this.tree, this.selectedId) || [] : [];
    },
    isDirty() {
      if (!this.originalForm) return false;
      return JSON.stringify(this.form) !== JSON.stringify(this.originalForm);
    },
    titleLen() {
      return (this.form.meta_title || "").length;
    },
    titleCounterClass() {
      if (this.titleLen === 0) return "muted";
      if (this.titleLen > 60) return "error";
      if (this.titleLen < 50) return "warning";
      return "ok";
    },
    descLen() {
      return (this.form.meta_description || "").length;
    },
    descCounterClass() {
      if (this.descLen === 0) return "muted";
      if (this.descLen > 160) return "error";
      if (this.descLen < 130) return "warning";
      return "ok";
    },
    explanationTextLength() {
      return this.stripHtml(this.form.explanation || "").length;
    },
    previewUrl() {
      return this.selectedId ? `${STORE_BASE_URL}/products?categories=${this.selectedId}` : `${STORE_BASE_URL}/products`;
    },
    previewTitle() {
      const t = (this.form.meta_title || "").trim();
      return t || (this.selectedTreeNode ? this.selectedTreeNode.name : "");
    },
    previewDescription() {
      const d = (this.form.meta_description || "").trim();
      return d || "توضیحاتی برای این دسته‌بندی ثبت نشده است.";
    },
    seoChecks() {
      const descText = this.stripHtml(this.form.explanation || "").trim();
      return [
        { id: "title_length", label: "طول عنوان مناسب", pass: this.titleLen >= 30 && this.titleLen <= 60 },
        { id: "description_length", label: "طول توضیحات مناسب", pass: this.descLen >= 70 && this.descLen <= 160 },
        { id: "category_description", label: "توضیحات دسته‌بندی ثبت شده", pass: descText.length > 0 },
        { id: "indexability", label: "صفحه قابل ایندکس است", pass: !!this.form.is_indexed },
      ];
    },
    seoScore() {
      const passed = this.seoChecks.filter((c) => c.pass).length;
      return Math.round((passed / this.seoChecks.length) * 100);
    },
    seoScoreColor() {
      if (this.seoScore >= 80) return "#35C88A";
      if (this.seoScore >= 50) return "#EFB44E";
      return "#F05F68";
    },
    saveStatusText() {
      if (this.saving) return "در حال ذخیره...";
      if (!this.selectedId) return "";
      return this.isDirty ? "تغییرات ذخیره‌نشده دارید" : "تمام تغییرات ذخیره شده‌اند";
    },
    saveStatusClass() {
      if (this.saving) return "saving";
      if (!this.selectedId) return "";
      return this.isDirty ? "unsaved" : "saved";
    },
  },
  created() {
    this.setDocumentTitle && this.setDocumentTitle("سئو دسته‌بندی");
    this.fetchTree();
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
  methods: {
    emptyForm() {
      return {
        meta_title: "",
        meta_description: "",
        is_indexed: true,
        is_followed: true,
        explanation: "",
      };
    },
    onEditorReady(quill) {
      quill.root.setAttribute("dir", "rtl");
      quill.root.style.textAlign = "right";
    },
    stripHtml(html) {
      return String(html || "").replace(/<[^>]*>/g, "");
    },
    findNodeById(nodes, id) {
      for (const n of nodes) {
        if (n.id === id) return n;
        if (n.children && n.children.length) {
          const found = this.findNodeById(n.children, id);
          if (found) return found;
        }
      }
      return null;
    },
    findPathNames(nodes, id, trail) {
      trail = trail || [];
      for (const n of nodes) {
        const nextTrail = trail.concat([n.name]);
        if (n.id === id) return nextTrail;
        if (n.children && n.children.length) {
          const found = this.findPathNames(n.children, id, nextTrail);
          if (found) return found;
        }
      }
      return null;
    },
    fetchTree() {
      this.loadingTree = true;
      const options = {
        url: this.endpoint("product/categorizationTree"),
        method: "get",
        success: (data) => {
          this.tree = data || [];
          this.loadingTree = false;
        },
        error: (err) => {
          this.loadingTree = false;
          this.handleError(err, options);
        },
      };
      this.request(options);
    },
    onPickCategory(node) {
      if (this.selectedId === node.id) {
        this.mobileDrawerOpen = false;
        return;
      }
      if (this.isDirty) {
        this.confirmDialog = { open: true, pendingNode: node };
        return;
      }
      this.applySelection(node);
    },
    applySelection(node) {
      this.mobileDrawerOpen = false;
      this.selectedId = node.id;
      this.loadingDetail = true;
      const options = {
        url: this.endpoint(`product/categorization/${node.id}`),
        method: "get",
        success: (data) => {
          this.form = {
            meta_title: data.meta_title || "",
            meta_description: data.meta_description || "",
            is_indexed: data.is_indexed !== false,
            is_followed: data.is_followed !== false,
            explanation: data.explanation || "",
          };
          this.originalForm = JSON.parse(JSON.stringify(this.form));
          this.loadingDetail = false;
        },
        error: (err) => {
          this.loadingDetail = false;
          this.handleError(err, options);
        },
      };
      this.request(options);
    },
    confirmSaveAndContinue() {
      const pending = this.confirmDialog.pendingNode;
      this.confirmDialog = { open: false, pendingNode: null };
      this.save(() => {
        if (pending) this.applySelection(pending);
      });
    },
    confirmContinueWithoutSaving() {
      const pending = this.confirmDialog.pendingNode;
      this.confirmDialog = { open: false, pendingNode: null };
      if (pending) this.applySelection(pending);
    },
    confirmCancel() {
      this.confirmDialog = { open: false, pendingNode: null };
    },
    save(onDone) {
      if (!this.selectedId) return;
      const node = this.findNodeById(this.tree, this.selectedId);
      this.saving = true;
      const options = {
        url: this.endpoint(`product/categorization/${this.selectedId}`),
        method: "put",
        data: {
          name: node ? node.name : undefined,
          meta_title: this.form.meta_title,
          meta_description: this.form.meta_description,
          is_indexed: this.form.is_indexed,
          is_followed: this.form.is_followed,
          explanation: this.form.explanation,
        },
        success: () => {
          this.saving = false;
          this.originalForm = JSON.parse(JSON.stringify(this.form));
          this.notify && this.notify("ذخیره شد", "success");
          if (onDone) onDone();
        },
        error: (err) => {
          this.saving = false;
          this.handleError(err, options);
        },
      };
      this.request(options);
    },
    onKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        if (this.selectedId && !this.saving) this.save();
        return;
      }
      if (e.key === "/") {
        const el = document.activeElement;
        const tag = el && el.tagName;
        const isTyping = tag === "INPUT" || tag === "TEXTAREA" || (el && el.isContentEditable);
        if (!isTyping) {
          e.preventDefault();
          this.$refs.pickerPanel && this.$refs.pickerPanel.focusSearch();
        }
      }
    },
    handleBeforeUnload(e) {
      if (this.isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    },
  },
};
</script>

<style scoped>
.csx-page {
  background: #07101f;
  min-height: 100vh;
  margin: -16px;
  padding: 24px 20px 100px;
  font-size: 13px;
  color: #f8fafc;
}
@media (min-width: 768px) {
  .csx-page {
    padding: 28px 32px 110px;
  }
}

.csx-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.csx-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: #6e7b94;
  margin-bottom: 10px;
}
.csx-breadcrumb-sep {
  color: #384c70;
}
.csx-breadcrumb-current {
  color: #a8b2c7;
}
.csx-title {
  font-size: 22px;
  font-weight: 800;
  color: #f8fafc;
  margin: 0 0 4px;
}
@media (min-width: 768px) {
  .csx-title {
    font-size: 26px;
  }
}
.csx-subtitle {
  font-size: 12.5px;
  color: #a8b2c7;
  margin: 0;
}
.csx-btn-secondary {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  background: #111d32;
  border: 1px solid #273753;
  border-radius: 10px;
  color: #a8b2c7;
  font-size: 12px;
  cursor: pointer;
}
.csx-btn-secondary svg {
  width: 15px;
  height: 15px;
}
.csx-btn-secondary:hover {
  border-color: #384c70;
  color: #f8fafc;
}

.csx-mobile-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 44px;
  padding: 0 14px;
  background: #0d1729;
  border: 1px solid #273753;
  border-radius: 12px;
  color: #f8fafc;
  font-size: 12.5px;
  margin-bottom: 14px;
  cursor: pointer;
}
.csx-mobile-trigger svg {
  width: 16px;
  height: 16px;
  color: #b04cff;
  flex-shrink: 0;
}
.csx-mobile-trigger-name {
  margin-inline-start: auto;
  color: #a8b2c7;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 40%;
}
@media (min-width: 1024px) {
  .csx-mobile-trigger {
    display: none;
  }
}

.csx-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
@media (min-width: 1024px) {
  .csx-layout {
    /* Flexbox rather than CSS Grid: grid's RTL track placement combined
       with a sticky child is inconsistent across browser engines; flexbox
       row-reversal under dir=rtl is far more reliably implemented. */
    flex-direction: row;
    align-items: flex-start;
  }
}

.csx-panel-slot {
  display: none;
}
@media (min-width: 1024px) {
  .csx-panel-slot {
    display: block;
    flex: 0 0 420px;
    width: 420px;
    /* Deliberately not `position: sticky` here — this app scrolls an inner
       container (not the window), and sticky-in-flex on a non-window
       scroller is a known source of cross-browser breakage (confirmed
       broken in Firefox, fine in Chromium) where the panel silently loses
       its content/size. A plain flow panel that scrolls with the page is
       less convenient but reliably correct everywhere. */
    /* Higher than the app's right-side nav drawer (z-index: 6), which
       expands over page content on hover since it's mini/rail by default. */
    z-index: 10;
  }
}

.csx-main {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  flex: 1 1 0%;
}

.csx-empty-state {
  background: #0d1729;
  border: 1px solid #273753;
  border-radius: 16px;
  padding: 60px 20px;
  text-align: center;
  color: #6e7b94;
}
.csx-empty-state svg {
  width: 44px;
  height: 44px;
  margin: 0 auto 14px;
  color: #384c70;
}
.csx-empty-state h3 {
  font-size: 14.5px;
  font-weight: 700;
  color: #f8fafc;
  margin: 0 0 6px;
}
.csx-empty-state p {
  font-size: 12px;
  margin: 0;
  max-width: 360px;
  margin-inline: auto;
}

.csx-card {
  background: #0d1729;
  border: 1px solid #273753;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16);
}

.csx-skeleton-bar {
  background: linear-gradient(90deg, #111d32 25%, #182740 37%, #111d32 63%);
  background-size: 400% 100%;
  border-radius: 8px;
  animation: csx-shimmer 1.4s ease infinite;
}
@keyframes csx-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.csx-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}
.csx-card-header-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(117, 87, 255, 0.14);
  color: #7557ff;
}
.csx-card-header-icon svg {
  width: 18px;
  height: 18px;
}
.csx-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #f8fafc;
}
.csx-card-subtitle {
  font-size: 11.5px;
  color: #6e7b94;
  margin-top: 2px;
}

.csx-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #f8fafc;
  margin-bottom: 4px;
}
.csx-card-desc {
  font-size: 11.5px;
  color: #6e7b94;
  margin: 0 0 14px;
}

.csx-field {
  margin-bottom: 18px;
}
.csx-field:last-child {
  margin-bottom: 0;
}
.csx-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #a8b2c7;
  margin-bottom: 8px;
}
.csx-required {
  color: #f05f68;
}
.csx-input,
.csx-textarea {
  width: 100%;
  background: #111d32;
  border: 1px solid #273753;
  border-radius: 10px;
  color: #f8fafc;
  font-size: 12.5px;
  padding: 0 14px;
  font-family: inherit;
}
.csx-input {
  height: 48px;
}
.csx-textarea {
  min-height: 100px;
  padding: 12px 14px;
  resize: vertical;
  line-height: 1.7;
}
.csx-input:hover,
.csx-textarea:hover {
  border-color: #384c70;
}
.csx-input:focus,
.csx-textarea:focus {
  outline: none;
  border-color: #7557ff;
  box-shadow: 0 0 0 3px rgba(117, 87, 255, 0.14);
}
.csx-input::placeholder,
.csx-textarea::placeholder {
  color: #6e7b94;
}

.csx-field-footer {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}
.csx-helper {
  font-size: 11px;
  color: #6e7b94;
  line-height: 1.6;
}
.csx-counter {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #6e7b94;
  white-space: nowrap;
}
.csx-counter.ok {
  color: #35c88a;
}
.csx-counter.warning {
  color: #efb44e;
}
.csx-counter.error {
  color: #f05f68;
}

.csx-editor-wrap {
  border: 1px solid #273753;
  border-radius: 10px;
  overflow: hidden;
  max-width: 100%;
}
.csx-editor-wrap ::v-deep .ql-toolbar {
  background: #111d32;
  border: none;
  border-bottom: 1px solid #273753;
}
.csx-editor-wrap ::v-deep .ql-toolbar .ql-stroke {
  stroke: #a8b2c7;
}
.csx-editor-wrap ::v-deep .ql-toolbar .ql-fill {
  fill: #a8b2c7;
}
.csx-editor-wrap ::v-deep .ql-toolbar .ql-picker-label {
  color: #a8b2c7;
}
.csx-editor-wrap ::v-deep .ql-toolbar button:hover .ql-stroke,
.csx-editor-wrap ::v-deep .ql-toolbar button.ql-active .ql-stroke {
  stroke: #7557ff;
}
.csx-editor-wrap ::v-deep .ql-toolbar button:hover .ql-fill,
.csx-editor-wrap ::v-deep .ql-toolbar button.ql-active .ql-fill {
  fill: #7557ff;
}
.csx-editor-wrap ::v-deep .ql-picker-options {
  background: #111d32;
  border-color: #273753;
}
.csx-editor-wrap ::v-deep .ql-container {
  background: #0d1729;
  border: none;
  color: #f8fafc;
  font-family: inherit;
  min-height: 180px;
  max-width: 100%;
}
.csx-editor-wrap ::v-deep .ql-editor {
  min-height: 180px;
  direction: rtl;
  text-align: right;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.csx-editor-wrap ::v-deep .ql-editor.ql-blank::before {
  color: #6e7b94;
  font-style: normal;
  right: 15px;
  left: auto;
}
.csx-editor-wrap ::v-deep .ql-editor img {
  max-width: 100%;
  height: auto;
}
.csx-editor-wrap ::v-deep .ql-editor pre.ql-syntax {
  direction: ltr;
  text-align: left;
  overflow-x: auto;
  max-width: 100%;
}
.csx-editor-wrap ::v-deep .ql-editor blockquote {
  border-right: 3px solid #384c70;
  border-left: none;
  padding-right: 12px;
  padding-left: 0;
  margin-right: 0;
}
.csx-editor-wrap ::v-deep .ql-snow .ql-picker {
  color: #a8b2c7;
}
.csx-editor-wrap ::v-deep .ql-snow .ql-tooltip {
  background: #111d32;
  border-color: #273753;
  color: #f8fafc;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.3);
}
.csx-editor-wrap ::v-deep .ql-snow .ql-tooltip input[type="text"] {
  background: #0d1729;
  border-color: #273753;
  color: #f8fafc;
}

.csx-switches-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
@media (min-width: 640px) {
  .csx-switches-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: start;
  }
}
.csx-switch-row {
  background: #111d32;
  border: 1px solid #273753;
  border-radius: 12px;
  padding: 14px;
}
.csx-switch-control {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.csx-switch {
  flex-shrink: 0;
  width: 40px;
  height: 22px;
  border-radius: 999px;
  background: #273753;
  border: none;
  position: relative;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.15s ease;
}
.csx-switch.on {
  background: #7557ff;
}
.csx-switch-thumb {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: #f8fafc;
  transition: transform 0.15s ease;
}
.csx-switch.on .csx-switch-thumb {
  transform: translateX(-18px);
}
.csx-switch-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #f8fafc;
  display: flex;
  align-items: center;
  gap: 6px;
}
.csx-switch-tech {
  font-size: 10px;
  font-weight: 500;
  color: #6e7b94;
  background: #0d1729;
  border: 1px solid #273753;
  border-radius: 999px;
  padding: 1px 7px;
  direction: ltr;
}
.csx-switch-desc {
  font-size: 11px;
  color: #6e7b94;
  margin-top: 4px;
  line-height: 1.6;
}
.csx-risk-warning {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 10px;
  background: rgba(239, 180, 78, 0.12);
  border: 1px solid rgba(239, 180, 78, 0.35);
  border-radius: 8px;
  color: #efb44e;
  font-size: 11px;
  line-height: 1.6;
}
.csx-risk-warning svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  margin-top: 1px;
}

.csx-google-preview {
  background: #111d32;
  border: 1px solid #273753;
  border-radius: 12px;
  padding: 16px;
}
.csx-google-url {
  font-size: 11.5px;
  color: #35c88a;
  direction: ltr;
  text-align: right;
  margin-bottom: 4px;
}
.csx-google-title {
  font-size: 15px;
  color: #7ca9ff;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.csx-google-desc {
  font-size: 12px;
  color: #a8b2c7;
  line-height: 1.7;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.csx-score-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.csx-score-badge {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 2px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
}
.csx-score-checks {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.csx-score-checks li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6e7b94;
}
.csx-score-checks li.pass {
  color: #a8b2c7;
}
.csx-check-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 999px;
  padding: 2px;
}
.csx-check-icon.ok {
  color: #ffffff;
  background: #35c88a;
}
.csx-check-icon.no {
  color: #6e7b94;
  background: #162238;
}

.csx-save-bar {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 20px;
  background: rgba(13, 23, 41, 0.94);
  backdrop-filter: blur(12px);
  border-top: 1px solid #273753;
  z-index: 30;
}
.csx-save-status {
  font-size: 12px;
  color: #6e7b94;
  display: flex;
  align-items: center;
  gap: 8px;
}
.csx-save-status.saved {
  color: #35c88a;
}
.csx-save-status.unsaved {
  color: #efb44e;
}
.csx-save-status.saving {
  color: #a8b2c7;
}
.csx-spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(168, 178, 199, 0.25);
  border-top-color: #a8b2c7;
  animation: csx-spin 0.7s linear infinite;
}
@keyframes csx-spin {
  to {
    transform: rotate(360deg);
  }
}
.csx-save-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 20px;
  background: #7557ff;
  border: none;
  border-radius: 10px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
}
.csx-save-btn svg {
  width: 16px;
  height: 16px;
}
.csx-save-btn:hover:not(:disabled) {
  background: #6848f4;
}
.csx-save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.csx-drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 16, 0.72);
  z-index: 40;
  display: flex;
  justify-content: flex-end;
}
.csx-drawer {
  width: min(340px, 88vw);
  height: 100%;
  background: #07101f;
  border-inline-start: 1px solid #273753;
  padding: 16px;
  overflow-y: auto;
}
.csx-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  color: #f8fafc;
  font-size: 13px;
  font-weight: 700;
}
.csx-icon-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111d32;
  border: 1px solid #273753;
  border-radius: 8px;
  color: #a8b2c7;
  cursor: pointer;
}
.csx-icon-btn svg {
  width: 14px;
  height: 14px;
}

.csx-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 16, 0.72);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.csx-modal {
  width: 100%;
  max-width: 420px;
  background: #0d1729;
  border: 1px solid #273753;
  border-radius: 16px;
  padding: 22px;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.4);
}
.csx-modal-title {
  font-size: 15px;
  font-weight: 700;
  color: #f8fafc;
  margin-bottom: 8px;
}
.csx-modal-desc {
  font-size: 12px;
  color: #a8b2c7;
  line-height: 1.8;
  margin-bottom: 18px;
}
.csx-help-text p {
  margin: 0 0 10px;
}
.csx-help-text p:last-child {
  margin-bottom: 0;
}
.csx-modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.csx-modal-btn {
  height: 38px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: #111d32;
  border: 1px solid #273753;
  color: #a8b2c7;
}
.csx-modal-btn:hover {
  border-color: #384c70;
  color: #f8fafc;
}
.csx-modal-btn.primary {
  background: #7557ff;
  border-color: #7557ff;
  color: #ffffff;
}
.csx-modal-btn.primary:hover {
  background: #6848f4;
}
.csx-modal-btn.ghost {
  background: transparent;
  border-color: transparent;
}
</style>
